import { h, Fragment } from "preact";
import { useState, useEffect, useMemo, useRef } from "preact/hooks";
import { createPortal } from "preact/compat";
import toastr from "toastr";
import { callAPI, reportException } from "../../lib/transceiver";
import { getItem } from "../../lib/myStore";

/*
  Modification: Vehicle Master modal - add new Model / Fuel Type / Variant (Insurance Head)
  APIs:
    GET  /v1/vehicleMaster?brand=          -> models, fuelTypesByModel, allFuelTypes, variantsByModel
    POST /v1/vehicleMaster/model           -> { brand, modelName }
    POST /v1/vehicleMaster/fuelType        -> { brand, modelName, fuelType }
    POST /v1/vehicleMaster/variant         -> { brand, modelName, fuelType, variants: [] }
*/

const fileName = "vehicleMasterModal";

const TABS = [
  { key: "model", label: "Model" },
  { key: "fuelType", label: "Fuel Type" },
  { key: "variant", label: "Variant" }
];

// "Crystal Honda" -> "Honda", "Crystal Kia" -> "Kia"
export const getBrandFromUserInfo = (userInfo = {}) => {
  const dealershipName = String(userInfo.userDealershipName || "").toLowerCase();
  if (dealershipName.includes("kia")) return "Kia";
  if (dealershipName.includes("honda")) return "Honda";
  return "";
};

const normalize = (value) => String(value || "").trim().toLowerCase();
const existsIn = (list, value) => (list || []).some((item) => normalize(item) === normalize(value));

const getErrorMessage = (HTTPException, fallback) => {
  const data = HTTPException && HTTPException.response && HTTPException.response.data;
  return (data && (data.message || data.error)) || fallback;
};

/* ---------- styles ---------- */
const S = {
  overlay: "position: fixed; inset: 0; z-index: 1000; background: rgba(6, 20, 31, 0.45); display: flex; align-items: center; justify-content: center; padding: 16px;",
  modal: "position: relative; width: 760px; max-width: 100%; max-height: 90vh; background: #fff; border-radius: 10px; box-shadow: 0 12px 40px rgba(0,0,0,.25); display: flex; flex-direction: column; overflow: hidden;",
  header: "display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #DFDFDF;",
  title: "font-size: 17px; font-weight: 500; color: #06141f; margin: 0;",
  brandPill: "margin-left: 10px; padding: 2px 10px; border-radius: 12px; font-size: 12px; background: #E3F3F4; color: #2E8994; font-weight: 500;",
  close: "width: 32px; height: 32px; margin: 0; padding: 0; border: none; background: transparent; font-size: 24px; line-height: 32px; color: #808080; cursor: pointer; border-radius: 6px;",
  tabs: "display: flex; border-bottom: 1px solid #DFDFDF; background: #FAFBFC; padding: 0 12px;",
  tab: "padding: 12px 18px; font-size: 14px; color: #808080; cursor: pointer; border-bottom: 3px solid transparent; user-select: none;",
  tabActive: "color: #002c5f; font-weight: 500; border-bottom-color: #2E8994;",
  body: "padding: 20px; overflow-y: auto; flex: 1;",
  label: "display: block; font-size: 12px; color: #808080; margin-bottom: 6px; font-weight: 500;",
  input: "width: 100%; box-sizing: border-box; height: 40px; margin: 0; padding: 0 12px; font-size: 14px; line-height: 20px; color: #06141f; border: 1px solid #CFCFCF; border-radius: 6px; outline: none; background: #fff; font-family: inherit;",
  searchInput: "height: 32px; width: 200px; padding: 0 10px; font-size: 12px;",
  textarea: "width: 100%; box-sizing: border-box; padding: 9px 12px; font-size: 14px; color: #06141f; border: 1px solid #CFCFCF; border-radius: 6px; outline: none; min-height: 110px; resize: vertical; font-family: inherit;",
  row: "display: flex; gap: 16px; margin-bottom: 16px; flex-wrap: wrap;",
  col: "flex: 1; min-width: 220px;",
  section: "margin-bottom: 18px;",
  sectionHead: "display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;",
  sectionTitle: "font-size: 13px; color: #242424; font-weight: 500;",
  chipBox: "display: flex; flex-wrap: wrap; gap: 6px; max-height: 170px; overflow-y: auto; padding: 10px; border: 1px solid #EEE; border-radius: 6px; background: #FAFBFC;",
  chip: "padding: 4px 10px; border-radius: 14px; font-size: 12px; background: #fff; border: 1px solid #DFDFDF; color: #242424;",
  chipNew: "padding: 4px 10px; border-radius: 14px; font-size: 12px; background: #E4F4DF; border: 1px solid #9BCB8C; color: #2F7D1E;",
  chipDup: "padding: 4px 10px; border-radius: 14px; font-size: 12px; background: #FCE4E4; border: 1px solid #EBA3A3; color: #C62828; text-decoration: line-through;",
  chipClickable: "cursor: pointer;",
  empty: "font-size: 12px; color: #9A9A9A; padding: 4px 2px;",
  error: "font-size: 12px; color: #C62828; margin-top: 6px;",
  hint: "font-size: 12px; color: #808080; margin-top: 6px;",
  footer: "display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid #DFDFDF; background: #FAFBFC;",
  btnPrimary: "height: 38px; margin: 0; padding: 0 18px; line-height: 20px; border-radius: 6px; border: 1px solid #2E8994; background: #2E8994; color: #fff; font-size: 14px; cursor: pointer; font-family: inherit;",
  btnSecondary: "height: 38px; margin: 0; padding: 0 18px; line-height: 20px; border-radius: 6px; border: 1px solid #CFCFCF; background: #fff; color: #242424; font-size: 14px; cursor: pointer; font-family: inherit;",
  btnDisabled: "opacity: .5; cursor: not-allowed;",
  confirmOverlay: "position: absolute; inset: 0; z-index: 2; background: rgba(255,255,255,.85); display: flex; align-items: center; justify-content: center; padding: 16px;",
  confirmBox: "width: 440px; max-width: 100%; background: #fff; border: 1px solid #DFDFDF; border-radius: 10px; box-shadow: 0 8px 30px rgba(0,0,0,.18); padding: 20px;",
  confirmTitle: "font-size: 16px; font-weight: 500; color: #06141f; margin: 0 0 10px;",
  confirmText: "font-size: 13px; color: #242424; margin: 0 0 8px; line-height: 1.5;",
  loading: "padding: 40px; text-align: center; color: #808080; font-size: 14px;"
};

/*
  Custom dropdown (replaces native <select>, whose height/padding is overridden by global app CSS)
  - searchable list, selected tick, keyboard: ArrowUp/ArrowDown/Enter/Escape, closes on outside click
*/
const SB = {
  wrap: "position: relative; width: 100%;",
  trigger: "width: 100%; box-sizing: border-box; height: 40px; margin: 0; padding: 0 36px 0 12px; display: flex; align-items: center; font-size: 14px; line-height: 20px; color: #06141f; text-align: left; background: #fff; border: 1px solid #CFCFCF; border-radius: 6px; cursor: pointer; outline: none; position: relative; font-family: inherit;",
  triggerOpen: "border-color: #2E8994; box-shadow: 0 0 0 3px rgba(46,137,148,.15);",
  triggerDisabled: "background: #F5F5F5; color: #9A9A9A; cursor: not-allowed;",
  value: "flex: 1; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;",
  placeholder: "color: #9A9A9A;",
  chevron: "position: absolute; right: 12px; top: 50%; width: 16px; height: 16px; margin-top: -8px; transition: transform .15s; pointer-events: none;",
  // fixed to viewport so the modal body's scroll area can't clip it (position set on open)
  panel: "position: fixed; z-index: 1100; background: #fff; border: 1px solid #DFDFDF; border-radius: 8px; box-shadow: 0 10px 28px rgba(0,0,0,.14); overflow: hidden;",
  search: "width: 100%; box-sizing: border-box; height: 36px; margin: 0; padding: 0 12px; font-size: 13px; border: none; border-bottom: 1px solid #EEE; outline: none; font-family: inherit;",
  list: "max-height: 220px; overflow-y: auto; padding: 4px 0; margin: 0; list-style: none;",
  option: "display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; font-size: 14px; line-height: 20px; color: #242424; cursor: pointer;",
  optionActive: "background: #F2FAFB;",
  optionSelected: "color: #002c5f; font-weight: 500;",
  empty: "padding: 10px 12px; font-size: 13px; color: #9A9A9A;"
};

const SelectBox = ({ value, options, placeholder, onChange, disabled, searchPlaceholder }) => {
  let [isOpen, setIsOpen] = useState(false);
  let [query, setQuery] = useState("");
  let [activeIndex, setActiveIndex] = useState(0);
  let [panelPosition, setPanelPosition] = useState("");
  const wrapRef = useRef();
  const panelRef = useRef();
  const searchRef = useRef();
  const listRef = useRef();
  const showSearch = (options || []).length > 6;

  const computePanelPosition = () => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const panelHeight = Math.min((options || []).length * 38 + 8 + (showSearch ? 36 : 0), 220 + (showSearch ? 36 : 0)) + 2;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < panelHeight + 12 && rect.top > spaceBelow;
    setPanelPosition(
      `left: ${rect.left}px; width: ${rect.width}px; ` +
        (openUp ? `bottom: ${window.innerHeight - rect.top + 4}px;` : `top: ${rect.bottom + 4}px;`)
    );
  };

  const filtered = query.trim()
    ? (options || []).filter((o) => normalize(o).includes(normalize(query)))
    : options || [];

  useEffect(() => {
    if (!isOpen) return;
    computePanelPosition();
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    };
    // fixed panel would detach from the trigger on scroll/resize -> close it
    const onScrollOrResize = (e) => {
      if (e && e.target && panelRef.current && panelRef.current.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("resize", onScrollOrResize);
    document.addEventListener("scroll", onScrollOrResize, true);
    const selectedIndex = Math.max(filtered.indexOf(value), 0);
    setActiveIndex(selectedIndex);
    setTimeout(() => {
      if (searchRef.current) searchRef.current.focus();
      const item = listRef.current && listRef.current.children[selectedIndex];
      if (item) item.scrollIntoView({ block: "nearest" });
    }, 0);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("scroll", onScrollOrResize, true);
      setPanelPosition("");
    };
  }, [isOpen]);

  const choose = (option) => {
    onChange(option);
    setIsOpen(false);
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen && ["Enter", " ", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.min(Math.max(activeIndex + (e.key === "ArrowDown" ? 1 : -1), 0), filtered.length - 1);
      setActiveIndex(next);
      const item = listRef.current && listRef.current.children[next];
      if (item) item.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter" && filtered[activeIndex] !== undefined) {
      e.preventDefault();
      choose(filtered[activeIndex]);
    }
  };

  return (
    <div style={SB.wrap} ref={wrapRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        style={`${SB.trigger} ${isOpen ? SB.triggerOpen : ""} ${disabled ? SB.triggerDisabled : ""}`}
        disabled={disabled}
        title={value || placeholder}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span style={`${SB.value} ${value ? "" : SB.placeholder}`}>{value || placeholder}</span>
        <svg style={`${SB.chevron} ${isOpen ? "transform: rotate(180deg);" : ""}`} viewBox="0 0 24 24" fill="none" stroke={disabled ? "#BFBFBF" : "#535c69"} stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && panelPosition && (
        <div style={`${SB.panel} ${panelPosition}`} ref={panelRef}>
          {showSearch && (
            <input
              ref={searchRef}
              style={SB.search}
              type="text"
              placeholder={searchPlaceholder || "Search"}
              value={query}
              onInput={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
            />
          )}
          {filtered.length ? (
            <ul style={SB.list} ref={listRef}>
              {filtered.map((option, index) => (
                <li
                  style={`${SB.option} ${index === activeIndex ? SB.optionActive : ""} ${option === value ? SB.optionSelected : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(option);
                  }}
                >
                  <span>{option}</span>
                  {option === value && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2E8994" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p style={SB.empty}>No results</p>
          )}
        </div>
      )}
    </div>
  );
};

const Chips = ({ items, emptyText, onChipClick, chipTitle }) =>
  items && items.length ? (
    <div style={S.chipBox}>
      {items.map((item) => (
        <span
          style={`${S.chip} ${onChipClick ? S.chipClickable : ""}`}
          title={chipTitle || item}
          onClick={() => onChipClick && onChipClick(item)}
        >
          {item}
        </span>
      ))}
    </div>
  ) : (
    <p style={S.empty}>{emptyText}</p>
  );

const VehicleMasterModal = ({ onClose }) => {
  const userInfo = getItem("userinfo") || {};
  const brand = getBrandFromUserInfo(userInfo);

  let [activeTab, setActiveTab] = useState("model");
  let [isLoading, setIsLoading] = useState(true);
  let [isSaving, setIsSaving] = useState(false);
  let [master, setMaster] = useState({ models: [], fuelTypesByModel: {}, allFuelTypes: [], variantsByModel: {} });
  let [confirmData, setConfirmData] = useState(null);

  // Model tab
  let [newModelName, setNewModelName] = useState("");
  let [modelSearch, setModelSearch] = useState("");
  // Fuel type tab
  let [fuelModel, setFuelModel] = useState("");
  let [newFuelType, setNewFuelType] = useState("");
  // Variant tab
  let [variantModel, setVariantModel] = useState("");
  let [variantFuel, setVariantFuel] = useState("");
  let [newVariantsText, setNewVariantsText] = useState("");
  let [variantSearch, setVariantSearch] = useState("");

  useEffect(() => {
    getVehicleMaster();
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const getVehicleMaster = async () => {
    if (!brand) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const response = await callAPI(`get`, `/v1/vehicleMaster`, `brand=${encodeURIComponent(brand)}`);
      if (response && response.data) {
        setMaster({
          models: response.data.models || [],
          fuelTypesByModel: response.data.fuelTypesByModel || {},
          allFuelTypes: response.data.allFuelTypes || [],
          variantsByModel: response.data.variantsByModel || {}
        });
      }
    } catch (HTTPException) {
      toastr.error(getErrorMessage(HTTPException, "Unable to load models"));
      await reportException({ HTTPException, fileName, functionName: "getVehicleMaster" });
    }
    setIsLoading(false);
  };

  /* ---------- derived values ---------- */
  const fuelTypesOfFuelModel = (fuelModel && master.fuelTypesByModel[fuelModel]) || [];
  const fuelTypesOfVariantModel = (variantModel && master.fuelTypesByModel[variantModel]) || [];

  const existingVariants = useMemo(() => {
    if (!variantModel || !variantFuel) return [];
    const byFuel = master.variantsByModel[variantModel] || {};
    const fuelKey = Object.keys(byFuel).find((fuel) => normalize(fuel) === normalize(variantFuel));
    return fuelKey ? byFuel[fuelKey] : [];
  }, [master, variantModel, variantFuel]);

  // one variant per line (commas also allowed), de-duplicated
  const parsedVariants = useMemo(() => {
    const list = [];
    newVariantsText
      .split(/\n|,/)
      .map((v) => v.trim())
      .filter(Boolean)
      .forEach((v) => {
        if (!existsIn(list, v)) list.push(v);
      });
    return list;
  }, [newVariantsText]);
  const duplicateVariants = parsedVariants.filter((v) => existsIn(existingVariants, v));
  const freshVariants = parsedVariants.filter((v) => !existsIn(existingVariants, v));

  const modelError = newModelName.trim() && existsIn(master.models, newModelName)
    ? `Model "${master.models.find((m) => normalize(m) === normalize(newModelName))}" already exists for ${brand}`
    : "";
  const fuelError = fuelModel && newFuelType.trim() && existsIn(fuelTypesOfFuelModel, newFuelType)
    ? `Fuel type "${newFuelType.trim()}" already exists for ${fuelModel}`
    : "";
  const variantError = duplicateVariants.length
    ? `${duplicateVariants.length} variant${duplicateVariants.length > 1 ? "s" : ""} already exist${duplicateVariants.length > 1 ? "" : "s"} for ${variantModel} - ${variantFuel}. Remove ${duplicateVariants.length > 1 ? "them" : "it"} to continue.`
    : "";

  const filteredModels = modelSearch.trim()
    ? master.models.filter((m) => normalize(m).includes(normalize(modelSearch)))
    : master.models;
  const filteredVariants = variantSearch.trim()
    ? existingVariants.filter((v) => normalize(v).includes(normalize(variantSearch)))
    : existingVariants;

  const canAddModel = brand && newModelName.trim() && !modelError && !isSaving;
  const canAddFuel = fuelModel && newFuelType.trim() && !fuelError && !isSaving;
  const canAddVariant = variantModel && variantFuel && freshVariants.length > 0 && !variantError && !isSaving;

  /* ---------- confirmation + save ---------- */
  const askConfirmation = () => {
    if (activeTab === "model" && canAddModel) {
      setConfirmData({
        title: "Add New Model",
        lines: [`Add model "${newModelName.trim()}" to ${brand}?`],
        action: saveModel
      });
    } else if (activeTab === "fuelType" && canAddFuel) {
      setConfirmData({
        title: "Add New Fuel Type",
        lines: [`Add fuel type "${newFuelType.trim()}" for model "${fuelModel}"?`],
        action: saveFuelType
      });
    } else if (activeTab === "variant" && canAddVariant) {
      setConfirmData({
        title: `Add ${freshVariants.length} Variant${freshVariants.length > 1 ? "s" : ""}`,
        lines: [`Model: ${variantModel}  |  Fuel Type: ${variantFuel}`],
        list: freshVariants,
        action: saveVariants
      });
    }
  };

  const runSave = async (apiPath, payload, functionName, onSuccess) => {
    try {
      setIsSaving(true);
      const response = await callAPI(`post`, apiPath, payload);
      toastr.success((response && response.data && response.data.message) || "Saved successfully");
      setConfirmData(null);
      onSuccess && onSuccess(response && response.data);
      await getVehicleMaster();
    } catch (HTTPException) {
      toastr.error(getErrorMessage(HTTPException, "Something went wrong"));
      setConfirmData(null);
      await reportException({ HTTPException, fileName, functionName });
    }
    setIsSaving(false);
  };

  const saveModel = () =>
    runSave(`/v1/vehicleMaster/model`, { brand, modelName: newModelName.trim() }, "saveModel", () => {
      setFuelModel(newModelName.trim());
      setNewModelName("");
    });

  const saveFuelType = () =>
    runSave(
      `/v1/vehicleMaster/fuelType`,
      { brand, modelName: fuelModel, fuelType: newFuelType.trim() },
      "saveFuelType",
      () => {
        setVariantModel(fuelModel);
        setVariantFuel(newFuelType.trim());
        setNewFuelType("");
      }
    );

  const saveVariants = () =>
    runSave(
      `/v1/vehicleMaster/variant`,
      { brand, modelName: variantModel, fuelType: variantFuel, variants: freshVariants },
      "saveVariants",
      () => setNewVariantsText("")
    );

  /* ---------- render helpers ---------- */
  const renderModelSelect = (value, onChange) => (
    <SelectBox
      value={value}
      options={master.models}
      placeholder="Select Model"
      searchPlaceholder="Search model"
      onChange={onChange}
    />
  );

  const renderModelTab = () => (
    <Fragment>
      <div style={S.section}>
        <label style={S.label}>New Model Name *</label>
        <input
          style={S.input}
          type="text"
          maxLength={100}
          placeholder={`e.g. ${brand === "Kia" ? "Sorento" : "Elevate"}`}
          value={newModelName}
          onInput={(e) => setNewModelName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && askConfirmation()}
        />
        {modelError ? (
          <p style={S.error}>{modelError}</p>
        ) : (
          <p style={S.hint}>The model will be added under brand {brand}. Add its fuel types next.</p>
        )}
      </div>
      <div style={S.section}>
        <div style={S.sectionHead}>
          <span style={S.sectionTitle}>Existing {brand} Models ({master.models.length})</span>
          <input
            style={`${S.input} ${S.searchInput}`}
            type="text"
            placeholder="Search model"
            value={modelSearch}
            onInput={(e) => setModelSearch(e.target.value)}
          />
        </div>
        <Chips items={filteredModels} emptyText="No models found" />
      </div>
    </Fragment>
  );

  const renderFuelTypeTab = () => (
    <Fragment>
      <div style={S.row}>
        <div style={S.col}>
          <label style={S.label}>Model *</label>
          {renderModelSelect(fuelModel, (value) => {
            setFuelModel(value);
            setNewFuelType("");
          })}
        </div>
        <div style={S.col}>
          <label style={S.label}>New Fuel Type *</label>
          <input
            style={S.input}
            type="text"
            maxLength={50}
            placeholder="e.g. CNG"
            disabled={!fuelModel}
            value={newFuelType}
            onInput={(e) => setNewFuelType(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && askConfirmation()}
          />
          {fuelError && <p style={S.error}>{fuelError}</p>}
        </div>
      </div>
      {fuelModel && (
        <div style={S.section}>
          <div style={S.sectionHead}>
            <span style={S.sectionTitle}>Fuel Types of {fuelModel} ({fuelTypesOfFuelModel.length})</span>
          </div>
          <Chips items={fuelTypesOfFuelModel} emptyText={`No fuel types added for ${fuelModel} yet`} />
        </div>
      )}
      <div style={S.section}>
        <div style={S.sectionHead}>
          <span style={S.sectionTitle}>All Fuel Types</span>
          <span style="font-size: 11px; color: #9A9A9A;">Click to use</span>
        </div>
        <Chips
          items={master.allFuelTypes}
          emptyText="No fuel types found"
          chipTitle="Click to use this fuel type"
          onChipClick={(fuel) => fuelModel && setNewFuelType(fuel)}
        />
      </div>
    </Fragment>
  );

  const renderVariantTab = () => (
    <Fragment>
      <div style={S.row}>
        <div style={S.col}>
          <label style={S.label}>Model *</label>
          {renderModelSelect(variantModel, (value) => {
            setVariantModel(value);
            setVariantFuel("");
            setVariantSearch("");
          })}
        </div>
        <div style={S.col}>
          <label style={S.label}>Fuel Type *</label>
          <SelectBox
            value={variantFuel}
            options={fuelTypesOfVariantModel}
            placeholder={variantModel ? "Select Fuel Type" : "Select model first"}
            disabled={!variantModel || !fuelTypesOfVariantModel.length}
            onChange={(fuel) => {
              setVariantFuel(fuel);
              setVariantSearch("");
            }}
          />
          {variantModel && !fuelTypesOfVariantModel.length && (
            <p style={S.error}>
              No fuel type for {variantModel}.{" "}
              <span
                style="color: #2E8994; text-decoration: underline; cursor: pointer;"
                onClick={() => {
                  setFuelModel(variantModel);
                  setActiveTab("fuelType");
                }}
              >
                Add fuel type
              </span>
            </p>
          )}
        </div>
      </div>
      {variantModel && variantFuel && (
        <Fragment>
          <div style={S.section}>
            <label style={S.label}>New Variants * (one per line)</label>
            <textarea
              style={S.textarea}
              placeholder={`${variantModel} D2.2 HTE 7\n${variantModel} D2.2 HTE(O) 7`}
              value={newVariantsText}
              onInput={(e) => setNewVariantsText(e.target.value)}
            />
            {parsedVariants.length > 0 && (
              <div style="margin-top: 8px; display: flex; flex-wrap: wrap; gap: 6px;">
                {parsedVariants.map((v) => (
                  <span
                    style={existsIn(existingVariants, v) ? S.chipDup : S.chipNew}
                    title={existsIn(existingVariants, v) ? "Already exists" : "New variant"}
                  >
                    {v}
                  </span>
                ))}
              </div>
            )}
            {variantError && <p style={S.error}>{variantError}</p>}
          </div>
          <div style={S.section}>
            <div style={S.sectionHead}>
              <span style={S.sectionTitle}>
                Existing Variants of {variantModel} - {variantFuel} ({existingVariants.length})
              </span>
              <input
                style={`${S.input} ${S.searchInput}`}
                type="text"
                placeholder="Search variant"
                value={variantSearch}
                onInput={(e) => setVariantSearch(e.target.value)}
              />
            </div>
            <Chips items={filteredVariants} emptyText="No variants added yet" />
          </div>
        </Fragment>
      )}
    </Fragment>
  );

  const canSubmit =
    (activeTab === "model" && canAddModel) ||
    (activeTab === "fuelType" && canAddFuel) ||
    (activeTab === "variant" && canAddVariant);

  const submitLabel =
    activeTab === "model"
      ? "Add Model"
      : activeTab === "fuelType"
      ? "Add Fuel Type"
      : `Add Variant${freshVariants.length > 1 ? `s (${freshVariants.length})` : ""}`;

  // Rendered into body so the left panel's layout/overflow can't clip the modal
  return createPortal(
    // Clicking outside (on the overlay) does not close the modal - use Close / × / Esc
    <div style={S.overlay}>
      <div style={S.modal} role="dialog" aria-modal="true">
        <div style={S.header}>
          <div style="display: flex; align-items: center;">
            <p style={S.title}>Vehicle Master</p>
            {brand && <span style={S.brandPill}>{brand}</span>}
          </div>
          <button style={S.close} title="Close" onClick={() => !isSaving && onClose && onClose()}>
            ×
          </button>
        </div>

        <div style={S.tabs}>
          {TABS.map((tab) => (
            <div
              style={`${S.tab} ${activeTab === tab.key ? S.tabActive : ""}`}
              onClick={() => !isSaving && setActiveTab(tab.key)}
            >
              {tab.label}
            </div>
          ))}
        </div>

        <div style={S.body}>
          {!brand ? (
            <p style={S.error}>
              Unable to identify brand (Honda / Kia) from your dealership "{userInfo.userDealershipName || "-"}".
            </p>
          ) : isLoading ? (
            <p style={S.loading}>Loading models...</p>
          ) : activeTab === "model" ? (
            renderModelTab()
          ) : activeTab === "fuelType" ? (
            renderFuelTypeTab()
          ) : (
            renderVariantTab()
          )}
        </div>

        <div style={S.footer}>
          <button style={S.btnSecondary} disabled={isSaving} onClick={() => onClose && onClose()}>
            Close
          </button>
          <button
            style={`${S.btnPrimary} ${canSubmit ? "" : S.btnDisabled}`}
            disabled={!canSubmit}
            onClick={askConfirmation}
          >
            {submitLabel}
          </button>
        </div>

        {confirmData && (
          <div style={S.confirmOverlay}>
            <div style={S.confirmBox}>
              <p style={S.confirmTitle}>{confirmData.title}</p>
              {confirmData.lines.map((line) => (
                <p style={S.confirmText}>{line}</p>
              ))}
              {confirmData.list && (
                <div style={`${S.chipBox} max-height: 140px; margin-bottom: 8px;`}>
                  {confirmData.list.map((item) => (
                    <span style={S.chipNew}>{item}</span>
                  ))}
                </div>
              )}
              <p style={`${S.confirmText} color: #808080; font-size: 12px;`}>
                This updates every form that uses this list.
              </p>
              <div style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px;">
                <button style={S.btnSecondary} disabled={isSaving} onClick={() => setConfirmData(null)}>
                  Cancel
                </button>
                <button
                  style={`${S.btnPrimary} ${isSaving ? S.btnDisabled : ""}`}
                  disabled={isSaving}
                  onClick={() => confirmData.action()}
                >
                  {isSaving ? "Saving..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default VehicleMasterModal;
